import React, { useState, useEffect } from 'react';
import { AthleteProfile } from '../types';
import {
  Trophy,
  Zap,
  Clock,
  Target,
  CheckCircle2,
  Sparkles,
  Play,
  RotateCcw,
  Award,
  ChevronRight,
  Flame,
  ShieldCheck,
  Crown,
  Volume2,
  VolumeX,
  History,
  BarChart3,
  Star
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell
} from 'recharts';

interface VolleyballChallengesViewProps {
  user: AthleteProfile;
  onEarnXp?: (amount: number, reason: string) => void;
  onUpdateProfile?: (updated: Partial<AthleteProfile>) => void;
}

export type DifficultyTier = 'Rookie' | 'Pro' | 'Legend';

interface TierConfig {
  name: DifficultyTier;
  targetMultiplier: number;
  xpMultiplier: number;
  color: string;
  badge: string;
}

const DIFFICULTY_TIERS: Record<DifficultyTier, TierConfig> = {
  Rookie: {
    name: 'Rookie',
    targetMultiplier: 1,
    xpMultiplier: 1,
    color: 'text-cyan-300 border-cyan-400/40 bg-cyan-950/60',
    badge: '🌱 Rookie Tier',
  },
  Pro: {
    name: 'Pro',
    targetMultiplier: 1.5,
    xpMultiplier: 1.5,
    color: 'text-purple-300 border-purple-400/50 bg-purple-950/70',
    badge: '⚡ Pro Tier',
  },
  Legend: {
    name: 'Legend',
    targetMultiplier: 2,
    xpMultiplier: 2.5,
    color: 'text-amber-300 border-amber-400/80 bg-amber-950/80',
    badge: '👑 Legend Tier',
  },
};

interface Drill {
  id: string;
  name: string;
  badgeTarget: 'Ace Striker' | 'Backcourt Wall' | 'Setting Maestro' | 'Net Wall';
  badgeIcon: string;
  badgeGoalName: string;
  description: string;
  timeLimitSeconds: number;
  baseTargetCount: number;
  baseXpReward: number;
  unit: string;
  accentColor: string;
}

const DRILL_PRESETS: Drill[] = [
  {
    id: 'drill_service_streak',
    name: 'Service Streak Challenge ⚡',
    badgeTarget: 'Ace Striker',
    badgeIcon: 'Zap',
    badgeGoalName: 'Ace Striker (50 Service Aces)',
    description: 'Land precision jump serves before the timer expires! Reach target aces to earn XP and unlock the Ace Striker badge.',
    timeLimitSeconds: 60,
    baseTargetCount: 10,
    baseXpReward: 350,
    unit: 'Aces',
    accentColor: 'from-amber-400 to-rose-500',
  },
  {
    id: 'drill_perfect_set',
    name: 'Perfect Set Drill 🎯',
    badgeTarget: 'Setting Maestro',
    badgeIcon: 'Target',
    badgeGoalName: 'Setting Maestro (30 Setting Assists)',
    description: 'Execute high-arc setting passes under time pressure. Deliver precision balls for hitters.',
    timeLimitSeconds: 45,
    baseTargetCount: 12,
    baseXpReward: 300,
    unit: 'Assists',
    accentColor: 'from-cyan-400 to-indigo-500',
  },
  {
    id: 'drill_backcourt_dig',
    name: 'Backcourt Dig Reflex 🛡️',
    badgeTarget: 'Backcourt Wall',
    badgeIcon: 'ShieldCheck',
    badgeGoalName: 'Backcourt Wall (500 Ground Digs)',
    description: 'Spike defense reaction drill! React to incoming high-speed attacks and log clean digs within the time window.',
    timeLimitSeconds: 60,
    baseTargetCount: 15,
    baseXpReward: 400,
    unit: 'Digs',
    accentColor: 'from-lime-400 to-emerald-500',
  },
];

interface DrillHistoryEntry {
  id: string;
  drillName: string;
  timestamp: string;
  tier: DifficultyTier;
  score: number;
  target: number;
  xpEarned: number;
  unit: string;
}

const INITIAL_DRILL_HISTORY: DrillHistoryEntry[] = [
  {
    id: 'hist_1',
    drillName: 'Service Streak Challenge ⚡',
    timestamp: '2026-08-06 17:42',
    tier: 'Pro',
    score: 15,
    target: 15,
    xpEarned: 525,
    unit: 'Aces',
  },
  {
    id: 'hist_2',
    drillName: 'Perfect Set Drill 🎯',
    timestamp: '2026-08-05 14:10',
    tier: 'Rookie',
    score: 12,
    target: 12,
    xpEarned: 300,
    unit: 'Assists',
  },
  {
    id: 'hist_3',
    drillName: 'Backcourt Dig Reflex 🛡️',
    timestamp: '2026-08-04 11:15',
    tier: 'Legend',
    score: 30,
    target: 30,
    xpEarned: 1000,
    unit: 'Digs',
  },
];

// Audio Web Audio API synthesizer helper for success and point feedback
const playSoundEffect = (type: 'point' | 'success', isAudioMuted: boolean) => {
  if (isAudioMuted) return;
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();

    if (type === 'point') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
      osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.08); // A5
      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.08);
    } else if (type === 'success') {
      // Arpeggio celebratory chord: C5 -> E5 -> G5 -> C6
      [523.25, 659.25, 783.99, 1046.50].forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + idx * 0.09);
        gain.gain.setValueAtTime(0.18, ctx.currentTime + idx * 0.09);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + idx * 0.09 + 0.28);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + idx * 0.09);
        osc.stop(ctx.currentTime + idx * 0.09 + 0.28);
      });
    }
  } catch (e) {
    // Web audio API not permitted or silent mode
  }
};

export const VolleyballChallengesView: React.FC<VolleyballChallengesViewProps> = ({
  user,
  onEarnXp,
  onUpdateProfile,
}) => {
  const [selectedDrill, setSelectedDrill] = useState<Drill>(DRILL_PRESETS[0]);
  const [selectedTier, setSelectedTier] = useState<DifficultyTier>('Rookie');
  const [isAudioMuted, setIsAudioMuted] = useState<boolean>(false);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(selectedDrill.timeLimitSeconds);
  const [currentScore, setCurrentScore] = useState<number>(0);
  const [drillHistory, setDrillHistory] = useState<DrillHistoryEntry[]>(INITIAL_DRILL_HISTORY);

  const [completedDrillModal, setCompletedDrillModal] = useState<{
    drillName: string;
    tier: DifficultyTier;
    score: number;
    target: number;
    xpEarned: number;
    badgeName: string;
    newBadgeCount: number;
  } | null>(null);

  // Computed Target & XP for active Tier
  const currentTarget = Math.round(selectedDrill.baseTargetCount * DIFFICULTY_TIERS[selectedTier].targetMultiplier);
  const currentXpReward = Math.round(selectedDrill.baseXpReward * DIFFICULTY_TIERS[selectedTier].xpMultiplier);

  // Timer countdown hook
  useEffect(() => {
    let timer: any = null;
    if (isRunning && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsRunning(false);
            checkDrillCompletion(currentScore, selectedDrill, currentTarget, currentXpReward, selectedTier);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isRunning, timeLeft, currentScore, selectedDrill, currentTarget, currentXpReward, selectedTier]);

  // Handle score increment during active drill
  const handleScorePoint = () => {
    if (!isRunning) return;
    const newScore = currentScore + 1;
    setCurrentScore(newScore);
    playSoundEffect('point', isAudioMuted);

    // Auto-win if target reached before timer ends
    if (newScore >= currentTarget) {
      setIsRunning(false);
      checkDrillCompletion(newScore, selectedDrill, currentTarget, currentXpReward, selectedTier);
    }
  };

  const startDrill = (drill: Drill) => {
    setSelectedDrill(drill);
    setCurrentScore(0);
    setTimeLeft(drill.timeLimitSeconds);
    setIsRunning(true);
    setCompletedDrillModal(null);
  };

  const resetDrill = () => {
    setIsRunning(false);
    setCurrentScore(0);
    setTimeLeft(selectedDrill.timeLimitSeconds);
    setCompletedDrillModal(null);
  };

  const checkDrillCompletion = (
    finalScore: number,
    drill: Drill,
    targetVal: number,
    xpVal: number,
    tierVal: DifficultyTier
  ) => {
    const isSuccess = finalScore >= targetVal;
    if (isSuccess) {
      playSoundEffect('success', isAudioMuted);
      const xp = xpVal;
      const updatedDrillCount = (user.volleyballDrillCount || 0) + 1;

      // Update badge progress stats in user object
      const currentVb = user.stats?.volleyball || { gamesPlayed: 0, kills: 0, aces: 0, blocks: 0, digs: 0 };
      let newAces = currentVb.aces || 0;
      let newDigs = currentVb.digs || 0;
      let newAssists = currentVb.assists || 0;

      if (drill.unit === 'Aces') newAces += finalScore;
      if (drill.unit === 'Digs') newDigs += finalScore;
      if (drill.unit === 'Assists') newAssists += finalScore;

      const updatedProfile: Partial<AthleteProfile> = {
        xp: user.xp + xp,
        volleyballDrillCount: updatedDrillCount,
        stats: {
          ...user.stats,
          volleyball: {
            ...currentVb,
            aces: newAces,
            digs: newDigs,
            assists: newAssists,
          },
        },
      };

      if (onUpdateProfile) {
        onUpdateProfile(updatedProfile);
      }
      if (onEarnXp) {
        onEarnXp(xp, `Completed Volleyball ${drill.name} (${tierVal})`);
      }

      // Add entry to Recent Drill History
      const newHistoryEntry: DrillHistoryEntry = {
        id: `hist_${Date.now()}`,
        drillName: drill.name,
        timestamp: new Date().toISOString().replace('T', ' ').slice(0, 16),
        tier: tierVal,
        score: finalScore,
        target: targetVal,
        xpEarned: xp,
        unit: drill.unit,
      };

      setDrillHistory((prev) => [newHistoryEntry, ...prev]);

      // Display Celebration Modal
      setCompletedDrillModal({
        drillName: drill.name,
        tier: tierVal,
        score: finalScore,
        target: targetVal,
        xpEarned: xp,
        badgeName: drill.badgeGoalName,
        newBadgeCount: drill.unit === 'Aces' ? newAces : drill.unit === 'Digs' ? newDigs : newAssists,
      });
    }
  };

  // Recharts completion percentage chart data
  const chartData = [
    {
      name: 'Service Aces',
      current: user.stats?.volleyball?.aces || 12,
      target: 50,
      percent: Math.min(100, Math.round(((user.stats?.volleyball?.aces || 12) / 50) * 100)),
      fill: '#F59E0B',
    },
    {
      name: 'Perfect Sets',
      current: user.stats?.volleyball?.assists || 18,
      target: 30,
      percent: Math.min(100, Math.round(((user.stats?.volleyball?.assists || 18) / 30) * 100)),
      fill: '#06B6D4',
    },
    {
      name: 'Ground Digs',
      current: user.stats?.volleyball?.digs || 28,
      target: 100,
      percent: Math.min(100, Math.round(((user.stats?.volleyball?.digs || 28) / 100) * 100)),
      fill: '#84CC16',
    },
    {
      name: 'Net Stuff Blocks',
      current: user.stats?.volleyball?.blocks || 15,
      target: 25,
      percent: Math.min(100, Math.round(((user.stats?.volleyball?.blocks || 15) / 25) * 100)),
      fill: '#A855F7',
    },
  ];

  return (
    <div className="space-y-6">
      {/* CELEBRATION MODAL WITH TIER-SPECIFIC SPECIAL ANIMATIONS */}
      {completedDrillModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
          <div
            className={`p-6 md:p-8 rounded-[2.5rem] max-w-lg w-full text-center space-y-6 relative overflow-hidden shadow-2xl transition-all ${
              completedDrillModal.tier === 'Legend'
                ? 'bg-gradient-to-br from-amber-950 via-purple-950 to-indigo-950 border-4 border-amber-400 shadow-[0_0_80px_rgba(251,191,36,0.6)] animate-pulse'
                : completedDrillModal.tier === 'Pro'
                ? 'bg-gradient-to-br from-purple-950 via-indigo-950 to-slate-900 border-2 border-purple-400 shadow-[0_0_50px_rgba(192,132,252,0.5)]'
                : 'bg-gradient-to-br from-indigo-950 via-purple-950 to-indigo-900 border-2 border-lime-400 shadow-[0_0_50px_rgba(163,230,53,0.5)]'
            }`}
          >
            {/* Sparkles / Gold Burst Effects */}
            <div className="absolute -top-10 -left-10 w-40 h-40 bg-amber-400/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-lime-400/20 rounded-full blur-3xl" />

            {/* Badge Icon Header */}
            <div className="inline-flex items-center justify-center p-4 bg-amber-400 text-black rounded-3xl shadow-xl animate-bounce">
              {completedDrillModal.tier === 'Legend' ? (
                <Crown className="w-12 h-12 stroke-[2.5]" />
              ) : (
                <Trophy className="w-10 h-10 stroke-[2.5]" />
              )}
            </div>

            <div className="space-y-2">
              <span className="px-3.5 py-1 bg-amber-400 text-black font-black text-xs uppercase font-mono rounded-full tracking-wider animate-pulse inline-flex items-center space-x-1">
                <Star className="w-3.5 h-3.5 fill-black" />
                <span>DRILL COMPLETE! {completedDrillModal.tier.toUpperCase()} CHAMPION</span>
              </span>
              <h2 className="text-2xl md:text-3xl font-black italic uppercase text-white">
                {completedDrillModal.drillName}
              </h2>
              <p className="text-xs text-indigo-200">
                Outstanding accuracy! You crushed the target of {completedDrillModal.target} reps under {selectedDrill.timeLimitSeconds} seconds.
              </p>
            </div>

            {/* Rewards Breakdown Box */}
            <div className="bg-indigo-950/90 border border-white/20 rounded-2xl p-4 space-y-3 text-left">
              <div className="flex items-center justify-between border-b border-white/10 pb-2.5">
                <span className="text-xs text-indigo-200 font-bold flex items-center space-x-1.5">
                  <Zap className="w-4 h-4 text-lime-400 fill-lime-400" />
                  <span>XP REWARD EARNED</span>
                </span>
                <span className="text-xl font-black text-lime-400 font-mono">
                  +{completedDrillModal.xpEarned} XP
                </span>
              </div>

              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-indigo-200 font-bold flex items-center space-x-1.5">
                  <Award className="w-4 h-4 text-amber-300" />
                  <span>BADGE PROGRESS BOOST</span>
                </span>
                <span className="text-xs font-mono font-black text-cyan-300">
                  {completedDrillModal.badgeName}: {completedDrillModal.newBadgeCount} Total Reps
                </span>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-3 pt-2">
              <button
                type="button"
                onClick={() => setCompletedDrillModal(null)}
                className="w-full py-3.5 bg-gradient-to-r from-amber-400 via-lime-400 to-emerald-400 hover:from-amber-300 hover:to-emerald-300 text-black font-black italic uppercase text-sm rounded-2xl shadow-xl transition"
              >
                Claim Rewards & Continue 🚀
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HEADER SECTION WITH AUDIO CONTROLS & DRILL COUNTER */}
      <div className="bg-gradient-to-br from-indigo-950 via-purple-950 to-indigo-900 p-6 md:p-8 rounded-[2.5rem] border-2 border-lime-400/40 shadow-2xl space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-5">
          <div>
            <div className="flex items-center space-x-2">
              <span className="px-2.5 py-1 bg-lime-400 text-black font-black text-[10px] uppercase font-mono rounded">
                VOLLEYBALL DRILL MODULE
              </span>
              <span className="text-xs text-indigo-200/80 font-bold">Audio & Multi-Tier Challenges</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-black italic uppercase text-white mt-1">
              Volleyball Skill Challenge Drills
            </h2>
            <p className="text-xs text-indigo-200/80 mt-1 max-w-xl">
              Execute timed Service Streak and Perfect Set drills. Adjust difficulty tiers (Rookie, Pro, Legend) for bonus XP multipliers and instant audio feedback confirmation!
            </p>
          </div>

          <div className="flex items-center space-x-3">
            {/* Audio Feedback Toggle */}
            <button
              type="button"
              onClick={() => setIsAudioMuted(!isAudioMuted)}
              className={`p-3 rounded-2xl border transition flex items-center space-x-2 text-xs font-mono font-bold ${
                !isAudioMuted
                  ? 'bg-lime-400/20 border-lime-400 text-lime-300'
                  : 'bg-indigo-950 border-white/10 text-indigo-300/60'
              }`}
              title="Toggle Audio Feedback"
            >
              {!isAudioMuted ? <Volume2 className="w-5 h-5 text-lime-400" /> : <VolumeX className="w-5 h-5" />}
              <span>{isAudioMuted ? 'Muted' : 'Audio ON'}</span>
            </button>

            <div className="flex items-center space-x-3 bg-indigo-900/80 p-3 rounded-2xl border border-white/10 shrink-0">
              <Trophy className="w-5 h-5 text-lime-400" />
              <div>
                <span className="text-[10px] text-indigo-300 font-mono block">DRILLS COMPLETED</span>
                <span className="text-lg font-black text-lime-400 font-mono">
                  {user.volleyballDrillCount || 0} Finished
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* DIFFICULTY TIER SELECTOR ROW */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs font-mono">
            <span className="text-indigo-200 font-bold">SELECT DIFFICULTY TIER:</span>
            <span className="text-lime-300">Higher Tiers = XP Multipliers!</span>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {(['Rookie', 'Pro', 'Legend'] as DifficultyTier[]).map((tierKey) => {
              const tierConf = DIFFICULTY_TIERS[tierKey];
              const isSelected = selectedTier === tierKey;

              return (
                <button
                  key={tierKey}
                  type="button"
                  onClick={() => {
                    setSelectedTier(tierKey);
                    resetDrill();
                  }}
                  className={`p-3.5 rounded-2xl border transition-all text-left flex flex-col justify-between ${
                    isSelected
                      ? `${tierConf.color} ring-2 ring-lime-400 shadow-xl scale-[1.02]`
                      : 'bg-indigo-950/60 border-white/10 text-indigo-200/70 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-extrabold text-sm">{tierConf.badge}</span>
                    <span className="text-[10px] font-mono font-black px-2 py-0.5 bg-black/40 rounded">
                      {tierConf.xpMultiplier}x XP
                    </span>
                  </div>
                  <span className="text-[11px] font-mono mt-1 opacity-80">
                    Target: {tierConf.targetMultiplier}x Reps
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ACTIVE DRILL ARENA */}
        <div className="bg-indigo-900/60 p-6 rounded-[2rem] border border-lime-400/30 space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-xs font-mono font-bold text-lime-300 uppercase">
                  ACTIVE SESSION:
                </span>
                <span className="px-2 py-0.5 bg-lime-400 text-black text-[10px] font-mono font-black rounded uppercase">
                  {selectedTier} Tier
                </span>
              </div>
              <h3 className="text-xl font-black italic text-white uppercase mt-1">
                {selectedDrill.name}
              </h3>
            </div>

            {/* Timer, Target and Reward Display */}
            <div className="flex items-center space-x-3 bg-indigo-950 px-4 py-2 rounded-2xl border border-white/10 font-mono">
              <div className="flex items-center space-x-1.5 text-rose-400">
                <Clock className="w-4 h-4 animate-pulse" />
                <span className="text-lg font-black">{timeLeft}s</span>
              </div>
              <span className="text-white/30">|</span>
              <div className="flex items-center space-x-1.5 text-lime-400">
                <Target className="w-4 h-4" />
                <span className="text-lg font-black">
                  {currentScore} / {currentTarget} {selectedDrill.unit}
                </span>
              </div>
              <span className="text-white/30">|</span>
              <span className="text-amber-300 text-sm font-black">+{currentXpReward} XP</span>
            </div>
          </div>

          {/* Drill Interactive Control Area */}
          <div className="bg-indigo-950/80 p-6 rounded-2xl border border-white/10 text-center space-y-5">
            <p className="text-xs text-indigo-200 max-w-md mx-auto">{selectedDrill.description}</p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              {!isRunning ? (
                <button
                  type="button"
                  onClick={() => startDrill(selectedDrill)}
                  className="px-6 py-3.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-sm rounded-2xl shadow-xl transition flex items-center space-x-2"
                >
                  <Play className="w-5 h-5 fill-black" />
                  <span>Start {selectedTier} Drill Timer ({selectedDrill.timeLimitSeconds}s)</span>
                </button>
              ) : (
                <div className="space-y-3 w-full max-w-xs mx-auto">
                  <button
                    type="button"
                    onClick={handleScorePoint}
                    className="w-full py-4 bg-gradient-to-r from-lime-400 via-emerald-400 to-cyan-400 text-black font-black italic uppercase text-base rounded-2xl shadow-2xl transition transform active:scale-95 flex items-center justify-center space-x-2 border-2 border-lime-300"
                  >
                    <Flame className="w-6 h-6 fill-black" />
                    <span>TAP TO LOG {selectedDrill.unit.toUpperCase()} (+1)</span>
                  </button>

                  <button
                    type="button"
                    onClick={resetDrill}
                    className="text-xs text-indigo-300 hover:text-white underline font-mono block mx-auto"
                  >
                    Reset Drill Session
                  </button>
                </div>
              )}
            </div>

            {/* Drill Progress Bar */}
            <div className="space-y-1.5 pt-2">
              <div className="flex justify-between text-xs font-mono text-indigo-300">
                <span>Progress toward target ({currentTarget} {selectedDrill.unit})</span>
                <span>{Math.round((currentScore / currentTarget) * 100)}%</span>
              </div>
              <div className="w-full bg-indigo-900 h-3 rounded-full overflow-hidden border border-white/10">
                <div
                  className="bg-gradient-to-r from-lime-400 to-cyan-400 h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(100, (currentScore / currentTarget) * 100)}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* PRESET DRILLS SELECTION GRID */}
        <div className="space-y-3">
          <h4 className="text-sm font-black italic uppercase text-white tracking-wider font-mono">
            SELECT DRILL TYPE
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {DRILL_PRESETS.map((drill) => {
              const isSelected = selectedDrill.id === drill.id;
              const drillTarget = Math.round(drill.baseTargetCount * DIFFICULTY_TIERS[selectedTier].targetMultiplier);
              const drillXp = Math.round(drill.baseXpReward * DIFFICULTY_TIERS[selectedTier].xpMultiplier);

              return (
                <div
                  key={drill.id}
                  onClick={() => startDrill(drill)}
                  className={`p-5 rounded-2xl border cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-indigo-950 border-lime-400 shadow-xl ring-2 ring-lime-400/40'
                      : 'bg-indigo-900/40 border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-black text-lime-300 font-mono uppercase">
                      ⏱️ {drill.timeLimitSeconds}s Timer
                    </span>
                    <span className="px-2.5 py-1 bg-lime-400/20 text-lime-300 font-mono font-black text-xs rounded-xl border border-lime-400/30">
                      +{drillXp} XP
                    </span>
                  </div>

                  <h5 className="font-extrabold text-white text-base mb-1">{drill.name}</h5>
                  <p className="text-xs text-indigo-200/70 mb-3">{drill.description}</p>

                  <div className="flex items-center justify-between text-xs font-mono pt-2 border-t border-white/10 text-indigo-300">
                    <span>Target: {drillTarget} {drill.unit}</span>
                    <span className="text-lime-400 font-bold flex items-center space-x-1">
                      <span>Select</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RECHARTS PROGRESS VISUALIZATION CHART & RECENT DRILL HISTORY TABLE */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-4 border-t border-white/10">
          {/* Recharts Bar Chart Container */}
          <div className="lg:col-span-6 bg-indigo-950/80 p-5 rounded-2xl border border-white/10 space-y-4">
            <div className="flex items-center space-x-2">
              <BarChart3 className="w-5 h-5 text-lime-400" />
              <h4 className="text-sm font-black italic uppercase text-white font-mono">
                Overall Drill Category Completion (%)
              </h4>
            </div>

            <div className="h-60 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <XAxis dataKey="name" stroke="#94A3B8" fontSize={11} tickLine={false} />
                  <YAxis stroke="#94A3B8" fontSize={11} domain={[0, 100]} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0F172A', borderColor: '#A3E635', borderRadius: '0.75rem', color: '#FFF' }}
                    formatter={(value: any) => [`${value}% Completion`, 'Progress']}
                  />
                  <Bar dataKey="percent" radius={[8, 8, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Recent Drill History Table */}
          <div className="lg:col-span-6 bg-indigo-950/80 p-5 rounded-2xl border border-white/10 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <History className="w-5 h-5 text-lime-400" />
                <h4 className="text-sm font-black italic uppercase text-white font-mono">
                  Recent Drill History
                </h4>
              </div>
              <span className="text-[10px] font-mono text-indigo-300">Last 10 Attempts</span>
            </div>

            <div className="overflow-x-auto max-h-60 overflow-y-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] font-black uppercase text-indigo-300 font-mono">
                    <th className="pb-2">Drill</th>
                    <th className="pb-2">Tier</th>
                    <th className="pb-2">Score</th>
                    <th className="pb-2 text-right">XP Earned</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-xs">
                  {drillHistory.map((item) => (
                    <tr key={item.id} className="hover:bg-white/5">
                      <td className="py-2.5 font-bold text-white">
                        <div>{item.drillName}</div>
                        <div className="text-[10px] text-indigo-300/60 font-mono">{item.timestamp}</div>
                      </td>
                      <td className="py-2.5">
                        <span className={`px-2 py-0.5 text-[10px] font-mono font-black rounded ${
                          DIFFICULTY_TIERS[item.tier].color
                        }`}>
                          {item.tier}
                        </span>
                      </td>
                      <td className="py-2.5 font-mono text-cyan-300 font-bold">
                        {item.score}/{item.target} {item.unit}
                      </td>
                      <td className="py-2.5 text-right font-black font-mono text-lime-400">
                        +{item.xpEarned} XP
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
